import { ApiError, adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * Translates an API error into a human-readable Portuguese sentence. Recognises every
 * `error` code the booking-ops endpoints emit; falls back to the raw message otherwise.
 */
function describeError(err: unknown): string {
  if (!(err instanceof ApiError)) return (err as Error)?.message ?? 'Erro desconhecido';
  switch (err.code) {
    case 'licence_not_valid': {
      const status = (err.payload.status as string) ?? 'missing';
      const reason = err.payload.reason as string | null;
      const label =
        status === 'missing'
          ? 'O cliente ainda não submeteu carta de condução.'
          : status === 'pending'
            ? 'A carta de condução está por validar.'
            : status === 'rejected'
              ? `Carta rejeitada${reason ? ` (${reason})` : ''}.`
              : 'Carta de condução não está em estado válido.';
      return `${label} Valide-a em Clientes → ${'<o cliente>'} antes de confirmar o levantamento.`;
    }
    case 'invalid_state':
      return `Acção indisponível neste estado da reserva (${err.payload.state}).`;
    case 'invalid_body':
      return `Dados inválidos: ${err.payload.detail ?? 'verifique os campos.'}`;
    case 'balance_already_paid':
      return 'O saldo já estava registado — não houve dupla cobrança.';
    case 'already_confirmed':
      return 'Este depósito já estava confirmado.';
    case 'no_deposit_payment':
      return 'Não há nenhum depósito pendente para esta reserva.';
    case 'rate_limited':
      return 'Demasiados pedidos seguidos. Aguarde um minuto.';
    case 'not_found':
      return 'Reserva não encontrada.';
    default:
      return err.message;
  }
}

type Action = 'cancel' | 'charge' | 'pickup' | 'return' | 'confirmDeposit';

interface Props {
  bookingId: string;
  state: string;
  plate: string;
  /** Deposit % frozen on this booking (005). The balance is the rest. */
  depositPct: number;
  /** Balance frozen in the booking's pricing snapshot; null for pre-snapshot bookings. */
  balance: { eur: number; cve: number } | null;
}

/**
 * Action sidebar for an admin booking. The 4 modals only enable the actions the FSM allows,
 * matching apps/api/src/routes/admin/booking-ops.ts and the canTransition map.
 */
export function BookingActions({ bookingId, state, plate, depositPct, balance }: Props) {
  const balancePct = 100 - depositPct;
  const [open, setOpen] = useState<Action | null>(null);

  // Pull the payments ledger once at the action-bar level so each button can reflect what's
  // already happened (deposit succeeded? balance succeeded?). Children reuse the same cache
  // key so there's no extra request.
  const { data: paymentsData } = useQuery({
    queryKey: ['admin', 'booking', bookingId, 'payments'],
    queryFn: () => adminApi.bookingPayments(bookingId),
  });
  const depositPaid = !!paymentsData?.items.find((p) => p.kind === 'deposit' && p.state === 'succeeded');
  const balancePaid = !!paymentsData?.items.find((p) => p.kind === 'balance' && p.state === 'succeeded');

  const canCancel = !['cancelled', 'refunded', 'completed'].includes(state);
  const canConfirmDeposit = state === 'reserved_pending_payment' && !depositPaid;
  // A 100% deposit leaves nothing to collect at the counter (005) — no balance action then.
  const nothingDue = balance !== null && balance.eur === 0 && balance.cve === 0;
  const canCharge = ['confirmed', 'in_progress'].includes(state) && !balancePaid && !nothingDue;
  const canPickup = state === 'confirmed';
  const canReturn = state === 'in_progress' || state === 'overdue';

  return (
    <aside style={{ display: 'grid', gap: 12 }}>
      <Btn
        label={depositPaid ? `Depósito ${depositPct}% pago ✓` : `Confirmar depósito ${depositPct}%`}
        onClick={() => setOpen('confirmDeposit')}
        disabled={!canConfirmDeposit}
        tone={depositPaid ? 'done' : undefined}
      />
      <Btn label="Confirmar levantamento" onClick={() => setOpen('pickup')} disabled={!canPickup} />
      <Btn
        label={balancePaid ? `Saldo ${balancePct}% pago ✓` : `Cobrar saldo ${balancePct}%`}
        onClick={() => setOpen('charge')}
        disabled={!canCharge}
        tone={balancePaid ? 'done' : undefined}
      />
      <Btn label="Processar devolução" onClick={() => setOpen('return')} disabled={!canReturn} />
      <Btn label="Cancelar reserva" tone="danger" onClick={() => setOpen('cancel')} disabled={!canCancel} />

      {open === 'cancel' && <CancelForm bookingId={bookingId} onClose={() => setOpen(null)} />}
      {open === 'confirmDeposit' && (
        <ConfirmDepositForm bookingId={bookingId} depositPct={depositPct} onClose={() => setOpen(null)} />
      )}
      {open === 'charge' && (
        <ChargeForm
          bookingId={bookingId}
          depositPct={depositPct}
          balance={balance}
          onClose={() => setOpen(null)}
        />
      )}
      {open === 'pickup' && <PickupForm bookingId={bookingId} plate={plate} onClose={() => setOpen(null)} />}
      {open === 'return' && <ReturnForm bookingId={bookingId} onClose={() => setOpen(null)} />}
    </aside>
  );
}

function ConfirmDepositForm({
  bookingId,
  depositPct,
  onClose,
}: { bookingId: string; depositPct: number; onClose: () => void }) {
  const refresh = useRefresh(bookingId);
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      adminApi.confirmDeposit(bookingId, {
        transactionId: transactionId || undefined,
        note: note || undefined,
      }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Modal onClose={onClose}>
      <Heading title="CONFIRMAR DEPÓSITO" sub={`Reconciliação manual do sinal de ${depositPct}%`} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        style={{ marginTop: 16, display: 'grid', gap: 12 }}
      >
        <p style={{ fontSize: 12, color: 'var(--lv-gray-500)', lineHeight: 1.5 }}>
          Use quando o cliente paga por transferência bancária e o comprovativo confere. Marca o pagamento
          como recebido, passa a reserva para CONFIRMED e envia o email de pagamento confirmado ao cliente.
        </p>
        <Field label="Referência da transferência (opcional)">
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            style={input}
            maxLength={120}
            placeholder="ex: TRF-2026-00345"
          />
        </Field>
        <Field label="Nota interna (opcional, audit log)">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            style={input}
          />
        </Field>
        {mut.error && <Err msg={describeError(mut.error)} />}
        <Actions onClose={onClose} pending={mut.isPending} label="Confirmar pagamento" />
      </form>
    </Modal>
  );
}

function Btn({
  label,
  onClick,
  disabled,
  tone,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'danger' | 'done';
}) {
  // `done` styling: muted green border + tinted background so completed steps stay visible
  // without competing with the still-actionable buttons. Distinct from `disabled` (no work
  // possible) and from `danger` (destructive).
  const background =
    disabled && tone === 'done'
      ? 'rgba(45, 122, 79, 0.08)'
      : disabled
        ? 'var(--lv-surface-soft)'
        : tone === 'danger'
          ? '#fff'
          : 'var(--lv-brand)';
  const color =
    disabled && tone === 'done'
      ? 'var(--lv-ok)'
      : disabled
        ? 'var(--lv-gray-500)'
        : tone === 'danger'
          ? 'var(--lv-err)'
          : '#fff';
  const border =
    tone === 'done' ? '1px solid rgba(45, 122, 79, 0.4)' : tone === 'danger' ? '1px solid var(--lv-err)' : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background,
        color,
        border,
        padding: '12px 14px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
      }}
    >
      {label}
    </button>
  );
}

// ────────────────────────────── modals ──────────────────────────────

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 35, 34, 0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: 480,
          padding: 28,
          border: '1px solid var(--lv-line)',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function useRefresh(bookingId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['admin', 'booking', bookingId] });
    qc.invalidateQueries({ queryKey: ['admin', 'bookings'] });
  };
}

function CancelForm({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const refresh = useRefresh(bookingId);
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState(true);
  const cancel = useMutation({
    mutationFn: () => adminApi.cancelBooking(bookingId, { reason, refund }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Modal onClose={onClose}>
      <Heading title="CANCELAR" sub="Cancelamento administrativo" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          cancel.mutate();
        }}
        style={{ marginTop: 16, display: 'grid', gap: 12 }}
      >
        <Field label="Motivo (obrigatório, é registado no audit log)">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            minLength={3}
            style={input}
          />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
          Marcar pagamentos como reembolsados
        </label>
        {cancel.error && <Err msg={describeError(cancel.error)} />}
        <Actions onClose={onClose} pending={cancel.isPending} label="Cancelar reserva" tone="danger" />
      </form>
    </Modal>
  );
}

function ChargeForm({
  bookingId,
  depositPct,
  balance,
  onClose,
}: {
  bookingId: string;
  depositPct: number;
  balance: Props['balance'];
  onClose: () => void;
}) {
  const refresh = useRefresh(bookingId);
  const [method, setMethod] = useState<'vinti4' | 'transfer' | 'cash' | 'multibanco'>('cash');
  const [amountEur, setAmountEur] = useState(balance?.eur ?? 0);
  const [amountCve, setAmountCve] = useState(balance?.cve ?? 0);
  const [transactionId, setTransactionId] = useState('');

  // Pre-fill with the balance frozen in the booking's snapshot. Bookings without one predate
  // 005 (20/80), so derive it from the deposit payment there: × (100 − pct) / pct = × 4.
  const balanceFactor = (100 - depositPct) / depositPct;
  const { data: paymentsData } = useQuery({
    queryKey: ['admin', 'booking', bookingId, 'payments'],
    queryFn: () => adminApi.bookingPayments(bookingId),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: only run once on data arrival
  useEffect(() => {
    if (balance || !paymentsData?.items.length) return;
    const deposit = paymentsData.items.find((p) => p.kind === 'deposit');
    if (!deposit) return;
    if (amountEur === 0) setAmountEur(Number((deposit.amountEur * balanceFactor).toFixed(2)));
    if (amountCve === 0) setAmountCve(Math.round(deposit.amountCve * balanceFactor));
  }, [paymentsData]);

  const charge = useMutation({
    mutationFn: () =>
      adminApi.chargeBalance(bookingId, {
        method,
        amountEur,
        amountCve,
        transactionId: transactionId || undefined,
      }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Modal onClose={onClose}>
      <Heading title={`COBRAR ${100 - depositPct}%`} sub="Saldo no balcão" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          charge.mutate();
        }}
        style={{ marginTop: 16, display: 'grid', gap: 12 }}
      >
        <Field label="Método">
          <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} style={input}>
            <option value="cash">Dinheiro</option>
            <option value="vinti4">Vinti4 / Multibanco</option>
            <option value="transfer">Transferência bancária</option>
            <option value="multibanco">Multibanco PT</option>
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="EUR">
            <input
              type="number"
              step="0.01"
              min="0"
              value={amountEur}
              onChange={(e) => setAmountEur(Number(e.target.value))}
              style={input}
            />
          </Field>
          <Field label="CVE">
            <input
              type="number"
              step="1"
              min="0"
              value={amountCve}
              onChange={(e) => setAmountCve(Number(e.target.value))}
              style={input}
            />
          </Field>
        </div>
        <p style={{ fontSize: 11, color: 'var(--lv-gray-500)', margin: 0 }}>
          Pré-preenchido com o saldo ({100 - depositPct}% do total). Pode deixar 0 numa das moedas se só
          recebeu na outra.
        </p>
        <Field label="TXN-ID (opcional)">
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            style={input}
            maxLength={120}
          />
        </Field>
        {charge.error && <Err msg={describeError(charge.error)} />}
        <Actions onClose={onClose} pending={charge.isPending} label="Registar pagamento" />
      </form>
    </Modal>
  );
}

function PickupForm({
  bookingId,
  plate,
  onClose,
}: { bookingId: string; plate: string; onClose: () => void }) {
  const refresh = useRefresh(bookingId);
  const [confirmPlate, setConfirmPlate] = useState(plate);
  const [notes, setNotes] = useState('');
  const pickup = useMutation({
    mutationFn: () => adminApi.pickupBooking(bookingId, { confirmPlate, notes: notes || undefined }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Modal onClose={onClose}>
      <Heading title="LEVANTAMENTO" sub="Confirmar entrega da viatura" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          pickup.mutate();
        }}
        style={{ marginTop: 16, display: 'grid', gap: 12 }}
      >
        <Field label="Matrícula entregue (deve corresponder à reserva)">
          <input
            type="text"
            value={confirmPlate}
            onChange={(e) => setConfirmPlate(e.target.value.toUpperCase())}
            style={input}
            maxLength={16}
            required
          />
        </Field>
        <Field label="Notas internas (opcional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={input} />
        </Field>
        <p style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>
          A API valida que a carta de condução está activa antes de mudar a reserva para EM CURSO.
        </p>
        {pickup.error && <Err msg={describeError(pickup.error)} />}
        <Actions onClose={onClose} pending={pickup.isPending} label="Confirmar levantamento" />
      </form>
    </Modal>
  );
}

function ReturnForm({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const refresh = useRefresh(bookingId);
  const [kmReturned, setKm] = useState(0);
  const [fuelLevel, setFuel] = useState<'empty' | 'quarter' | 'half' | 'three_quarters' | 'full'>('full');
  const [condition, setCondition] = useState<'ok' | 'minor_damage' | 'major_damage'>('ok');
  const [notes, setNotes] = useState('');
  const [chargeCaucao, setChargeCaucao] = useState(false);
  const [caucaoEur, setCaucaoEur] = useState(0);
  const [caucaoCve, setCaucaoCve] = useState(0);
  const [caucaoMethod, setCaucaoMethod] = useState<'vinti4' | 'transfer' | 'cash'>('cash');

  const ret = useMutation({
    mutationFn: () =>
      adminApi.returnBooking(bookingId, {
        kmReturned,
        fuelLevel,
        condition,
        notes: notes || undefined,
        caucao:
          chargeCaucao && (caucaoEur > 0 || caucaoCve > 0)
            ? { amountEur: caucaoEur, amountCve: caucaoCve, method: caucaoMethod }
            : undefined,
      }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  return (
    <Modal onClose={onClose}>
      <Heading title="DEVOLUÇÃO" sub="Encerrar reserva" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          ret.mutate();
        }}
        style={{ marginTop: 16, display: 'grid', gap: 12 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Km à devolução">
            <input
              type="number"
              min="0"
              value={kmReturned}
              onChange={(e) => setKm(Number(e.target.value))}
              style={input}
              required
            />
          </Field>
          <Field label="Combustível">
            <select
              value={fuelLevel}
              onChange={(e) => setFuel(e.target.value as typeof fuelLevel)}
              style={input}
            >
              <option value="full">Cheio</option>
              <option value="three_quarters">3/4</option>
              <option value="half">1/2</option>
              <option value="quarter">1/4</option>
              <option value="empty">Vazio</option>
            </select>
          </Field>
        </div>
        <Field label="Condição">
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value as typeof condition)}
            style={input}
          >
            <option value="ok">OK</option>
            <option value="minor_damage">Danos ligeiros</option>
            <option value="major_damage">Danos graves</option>
          </select>
        </Field>
        <Field label="Notas (opcional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={input} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={chargeCaucao} onChange={(e) => setChargeCaucao(e.target.checked)} />
          Cobrar caução / dano
        </label>
        {chargeCaucao && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="EUR">
              <input
                type="number"
                min="0"
                step="1"
                value={caucaoEur}
                onChange={(e) => setCaucaoEur(Number(e.target.value))}
                style={input}
              />
            </Field>
            <Field label="CVE">
              <input
                type="number"
                min="0"
                step="100"
                value={caucaoCve}
                onChange={(e) => setCaucaoCve(Number(e.target.value))}
                style={input}
              />
            </Field>
            <Field label="Método">
              <select
                value={caucaoMethod}
                onChange={(e) => setCaucaoMethod(e.target.value as typeof caucaoMethod)}
                style={input}
              >
                <option value="cash">Dinheiro</option>
                <option value="vinti4">Vinti4</option>
                <option value="transfer">Transferência</option>
              </select>
            </Field>
          </div>
        )}
        {ret.error && <Err msg={describeError(ret.error)} />}
        <Actions onClose={onClose} pending={ret.isPending} label="Encerrar reserva" />
      </form>
    </Modal>
  );
}

// ────────────────────────────── shared bits ──────────────────────────────

function Heading({ title, sub }: { title: string; sub: string }) {
  return (
    <>
      <div className="display" style={{ fontSize: 22 }}>
        {title}
      </div>
      <div className="label" style={{ color: 'var(--lv-brand)', marginTop: 4 }}>
        {sub}
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
        }}
      >
        {label}
      </span>
      <span style={{ marginTop: 4, display: 'block' }}>{children}</span>
    </label>
  );
}

function Err({ msg }: { msg: string }) {
  return (
    <div style={{ padding: '8px 12px', background: '#fce4e4', color: 'var(--lv-err)', fontSize: 12 }}>
      {msg}
    </div>
  );
}

function Actions({
  onClose,
  pending,
  label,
  tone,
}: {
  onClose: () => void;
  pending: boolean;
  label: string;
  tone?: 'danger';
}) {
  return (
    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <button type="button" onClick={onClose} style={btnGhost}>
        Fechar
      </button>
      <button type="submit" disabled={pending} style={tone === 'danger' ? btnDanger : btnPrimary}>
        {pending ? '…' : label}
      </button>
    </div>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  background: 'var(--lv-err)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
